/**
 * Tournament Entry repository (ADR-031 P3).
 * Path: arenas/{arenaId}/events/{eventId}/entries/{entryId}
 */

import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  getDocsFromServer,
  query,
  runTransaction,
  setDoc,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { touchEvent } from "./tournamentEventRepository";

function entriesCol(arenaId, eventId) {
  return collection(db, "arenas", arenaId, "events", eventId, "entries");
}

function entryRef(arenaId, eventId, entryId) {
  return doc(db, "arenas", arenaId, "events", eventId, "entries", entryId);
}

/**
 * @param {string} arenaId
 * @param {string} eventId
 * @param {{ fromServer?: boolean }} [opts]
 */
export async function listEntriesForEvent(arenaId, eventId, opts = {}) {
  if (!arenaId || !eventId) return [];
  const read = opts.fromServer ? getDocsFromServer : getDocs;
  const snap = await read(entriesCol(arenaId, eventId));
  return snap.docs.map((d) => ({
    entryId: d.id,
    eventId,
    arenaId,
    competitionId: arenaId,
    ...d.data(),
  }));
}

/**
 * @param {string} arenaId
 * @param {string[]} eventIds
 * @param {{ fromServer?: boolean }} [opts]
 */
export async function listEntriesForArena(arenaId, eventIds, opts = {}) {
  if (!arenaId) return [];
  const ids = Array.isArray(eventIds) ? eventIds : [];
  const nested = await Promise.all(
    ids.map((eventId) => listEntriesForEvent(arenaId, eventId, opts))
  );
  return nested.flat();
}

/**
 * @param {string} arenaId
 * @param {string} eventId
 * @param {object} entry
 */
export async function upsertEntry(arenaId, eventId, entry, opts = {}) {
  const entryId = entry.entryId;
  if (!arenaId || !eventId || !entryId) {
    throw new Error("arenaId, eventId, and entryId required.");
  }
  await setDoc(
    entryRef(arenaId, eventId, entryId),
    {
      ...entry,
      entryId,
      eventId,
      arenaId,
      competitionId: entry.competitionId || arenaId,
      updatedAt: serverTimestamp(),
      createdAt: entry.createdAt || serverTimestamp(),
    },
    { merge: true }
  );
  if (opts.touch !== false) await touchEvent(arenaId, eventId);
  return { ...entry, entryId, eventId, arenaId };
}

/**
 * Create this entry once. A second writer with the same id gets the saved entry.
 * Does not touch the parent event, so many players can register at once.
 * @param {string} arenaId
 * @param {string} eventId
 * @param {object} entry
 */
export async function claimEntry(arenaId, eventId, entry) {
  const entryId = entry.entryId;
  if (!arenaId || !eventId || !entryId) {
    throw new Error("arenaId, eventId, and entryId required.");
  }
  const ref = entryRef(arenaId, eventId, entryId);
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (snap.exists()) {
      const current = snap.data() || {};
      const status = current.status;
      if (status !== "withdrawn" && status !== "cancelled") {
        return {
          entryId,
          eventId,
          arenaId,
          competitionId: arenaId,
          ...current,
          existed: true,
        };
      }
    }
    const payload = {
      ...entry,
      entryId,
      eventId,
      arenaId,
      competitionId: entry.competitionId || arenaId,
      updatedAt: serverTimestamp(),
      createdAt: entry.createdAt || serverTimestamp(),
    };
    tx.set(ref, payload, { merge: true });
    return { ...entry, entryId, eventId, arenaId, existed: false };
  });
}

/**
 * Entries this player owns or partners, without reading the rest of the field.
 * @param {string} arenaId
 * @param {string[]} eventIds
 * @param {{ uid?: string | null, playerId?: string | null, fromServer?: boolean }} viewer
 */
export async function listEntriesForPlayer(arenaId, eventIds, viewer = {}) {
  if (!arenaId) return [];
  const ids = (Array.isArray(eventIds) ? eventIds : []).filter(Boolean);
  const uid = viewer.uid || null;
  const playerId = viewer.playerId || null;
  if (!ids.length || (!uid && !playerId)) return [];
  const read = viewer.fromServer ? getDocsFromServer : getDocs;
  const tasks = [];
  for (const eventId of ids) {
    // Membership only. createdBy is the organizer for entries they add,
    // so that query would list the whole field as "your registrations".
    if (uid) {
      tasks.push(
        read(query(entriesCol(arenaId, eventId), where("memberUserIds", "array-contains", uid))).then(
          (snap) =>
            snap.docs.map((d) => ({
              entryId: d.id,
              eventId,
              arenaId,
              competitionId: arenaId,
              ...d.data(),
            }))
        )
      );
    }
    if (playerId) {
      tasks.push(
        read(
          query(entriesCol(arenaId, eventId), where("memberPlayerIds", "array-contains", playerId))
        ).then((snap) =>
          snap.docs.map((d) => ({ entryId: d.id, eventId, arenaId, competitionId: arenaId, ...d.data() }))
        )
      );
    }
  }
  const nested = await Promise.all(tasks);
  const byId = new Map();
  for (const rows of nested) {
    for (const row of rows) byId.set(row.entryId, row);
  }
  return [...byId.values()];
}

/**
 * @param {string} arenaId
 * @param {string} eventId
 * @param {string} entryId
 */
export async function deleteEntry(arenaId, eventId, entryId) {
  if (!arenaId || !eventId || !entryId) return;
  await deleteDoc(entryRef(arenaId, eventId, entryId));
  await touchEvent(arenaId, eventId);
}

/**
 * Replace all entries for one Event (hard-cut write path).
 * @param {string} arenaId
 * @param {string} eventId
 * @param {object[]} entries
 */
export async function replaceEntriesForEvent(arenaId, eventId, entries) {
  const existing = await listEntriesForEvent(arenaId, eventId);
  const nextIds = new Set((entries || []).map((e) => e.entryId));
  await Promise.all(
    (entries || []).map((e) => upsertEntry(arenaId, eventId, { ...e, eventId }))
  );
  await Promise.all(
    existing
      .filter((e) => !nextIds.has(e.entryId))
      .map((e) => deleteEntry(arenaId, eventId, e.entryId))
  );
  return listEntriesForEvent(arenaId, eventId);
}
